'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { createProductReview } from '@eliadmin/core/src/api/products/product-api';
import { ProductRating } from '../detail/product-rating';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { DialogTitle } from '@eliadmin/core/src/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { LuPlus, LuX } from 'react-icons/lu';
import { type z } from 'zod';
import { object as __eliadmin_zod_0, string as __eliadmin_zod_1, number as __eliadmin_zod_2, array as __eliadmin_zod_3, boolean as __eliadmin_zod_4 } from 'zod';
import { ensureZodDefaults as __eliadmin_zod_defaults } from '@eliadmin/core/src/build/zod-defaults';
__eliadmin_zod_defaults();
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Field } from '@eliadmin/core/src/components/form/field';
import { AddReviewSuccess } from './add-review-success';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';

const MAX_POINTS = 5;

// TODO: add translations
export const addReviewSchema = (VM: FormValidationMessages) =>
  __eliadmin_zod_0({
    review: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    rating: __eliadmin_zod_2()
      .int()
      .min(1, VM.REVIEW_RATING_RANGE_VALIDATION_MESSAGE())
      .max(5, VM.REVIEW_RATING_RANGE_VALIDATION_MESSAGE()),
    positives: __eliadmin_zod_3(__eliadmin_zod_1()),
    negatives: __eliadmin_zod_3(__eliadmin_zod_1()),
    name: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: __eliadmin_zod_1().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    gdpr: __eliadmin_zod_4()
      .refine(val => val === true, { message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE() }),
  });

export type AddReviewDialogProps = {
  slug: string;
  name: string;
  className?: string;
};

export function AddReviewDialog(props: AddReviewDialogProps) {
  const { t } = useTranslations();
  const { me } = useCustomerStore();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();
  const { toast } = useToast();

  const queryClient = useQueryClient();
  const createReviewMutation = useMutation({ mutationFn: createProductReview });
  const { formValidationMessages } = useFormValidationMessages(t);

  const [positives, setPositives] = useState<string[]>(['']);
  const [negatives, setNegatives] = useState<string[]>(['']);
  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<z.infer<ReturnType<typeof addReviewSchema>>>({
    defaultValues: {
      rating: 1,
      review: '',
      positives: [''],
      negatives: [''],
      gdpr: true,
      name: me?.customer?.name || '',
      surname: me?.customer?.surname || '',
    },
    resolver: zodResolver(addReviewSchema(formValidationMessages)),
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (!me?.authToken) return;

    try {
      await createReviewMutation.mutateAsync({
        authToken: me.authToken,
        body: {
          review: data.review,
          rating: data.rating,
          positives: positives.filter(Boolean),
          negatives: negatives.filter(Boolean),
        } as any,
        locale,
        slug: props.slug,
      });
      router.refresh();
      queryClient.invalidateQueries({ queryKey: [CacheKey.PRODUCTS_REVIEWS] });
      setIsSuccess(true);
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text661'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  if (isSuccess) {
    return <AddReviewSuccess />;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'flex w-full flex-col items-center justify-center gap-6',
        'ea-add-review-dialog',
        props?.className,
      )}
    >
      <Form {...form}>
        <div className="flex w-full flex-col items-center justify-center gap-2">
          <DialogTitle className="flex w-full items-center justify-center">
            <Heading title={t('text2306')} center tag="h3" />
          </DialogTitle>
          <Paragraph size="base" color="foreground" className="w-full text-center">
            Napíšte recenziu pre produkt {props.name}. Pri vašom hodnotení bude zobrazené iba krstné
            meno.
          </Paragraph>
        </div>

        <FormField
          control={form.control}
          name="rating"
          render={({ field }) => (
            <FormItem required className={cn('col-span-2', 'ea-add-review-dialog__rating-field')}>
              <FormControl className={cn('', 'ea-add-review-dialog__rating-input')}>
                <div className="w-min">
                  <ProductRating
                    rating={field.value}
                    onRatingChange={rating => form.setValue(field.name, rating)}
                    hideTextRating
                    size={'lg'}
                    className={{
                      main: 'flex items-center justify-start gap-2',
                      wrapper: '',
                      starIcon: 'text-[#EDB000]',
                      starDisabled: 'text-[#DCDCDC]',
                    }}
                  />
                </div>
              </FormControl>
              <FormMessage className={cn('', 'ea-add-review-dialog__rating-message')} />
            </FormItem>
          )}
        />

        <div className="grid w-full grid-cols-12 gap-3 sm:gap-4">
          {/* name and surname prepared for when backend supports them */}
          {/* <Field required placeholder={t('text33')} name="name" control={form.control} className={{ wrapper: 'col-span-6 w-full' }} /> */}
          {/* <Field required placeholder={t('text34')} name="surname" control={form.control} className={{ wrapper: 'col-span-6 w-full' }} /> */}
          <Field
            required
            placeholder={t('text3775')}
            name="review"
            type="textarea"
            control={form.control}
            className={{ wrapper: 'col-span-12 w-full' }}
          />
          <div className="col-span-6 flex w-full flex-col items-start justify-start gap-3">
            {positives.map((value, index) => (
              <div key={index} className="flex w-full items-center gap-2">
                <Input
                  value={value}
                  onChange={e =>
                    setPositives(prev => prev.map((v, i) => (i === index ? e.target.value : v)))
                  }
                  placeholder={t('text3776')}
                  className={{ wrapper: 'flex-1' }}
                />
                {positives.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setPositives(prev => prev.filter((_, i) => i !== index))}
                    className="flex-shrink-0 text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <LuX size={16} />
                  </button>
                )}
              </div>
            ))}
            {positives.length < MAX_POINTS && (
              <div
                className="flex cursor-pointer items-center justify-start gap-2 transition-all hover:opacity-80"
                onClick={() => setPositives(prev => [...prev, ''])}
              >
                <LuPlus size={20} className="text-primary" />
                <p className="whitespace-nowrap text-sm text-primary">{t('text3778')}</p>
              </div>
            )}
          </div>
          <div className="col-span-6 flex w-full flex-col items-start justify-start gap-3">
            {negatives.map((value, index) => (
              <div key={index} className="flex w-full items-center gap-2">
                <Input
                  value={value}
                  onChange={e =>
                    setNegatives(prev => prev.map((v, i) => (i === index ? e.target.value : v)))
                  }
                  placeholder={t('text3777')}
                  className={{ wrapper: 'flex-1' }}
                />
                {negatives.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setNegatives(prev => prev.filter((_, i) => i !== index))}
                    className="flex-shrink-0 text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <LuX size={16} />
                  </button>
                )}
              </div>
            ))}
            {negatives.length < MAX_POINTS && (
              <div
                className="flex cursor-pointer items-center justify-start gap-2 transition-all hover:opacity-80"
                onClick={() => setNegatives(prev => [...prev, ''])}
              >
                <LuPlus size={20} className="text-primary" />
                <p className="whitespace-nowrap text-sm text-primary">{t('text3778')}</p>
              </div>
            )}
          </div>
        </div>
        {/* <FormField
          control={form.control}
          name="gdpr"
          render={({ field }) => (
            <FormItem
              className={cn(
                'flex w-full items-start',
                'ea-contact-form__gdpr-checkbox-item ea-contact-form__checkbox-item',
              )}
            >
              <FormControl>
                <Checkbox
                  ref={field.ref}
                  name={field.name}
                  disabled={field.disabled}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className={cn('', 'ea-contact-form__gdpr-checkbox ea-contact-form__checkbox')}
                />
              </FormControl>
              <FormLabel className={cn('ea-contact-form__gdpr-checkbox-label')}>
                <Paragraph size="lg" className="w-full">
                  {t('text613')}{' '}
                  <Link
                    href={getGdprUrl()}
                    target="_blank"
                    className="generic-hover font-bold text-primary"
                  >
                    {t('text614')}
                  </Link>{' '}
                  {t('text3354')}
                </Paragraph>
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        /> */}
        <Button className="w-full" disabled={createReviewMutation.isPending}>
          {t('text2306')}
        </Button>
      </Form>
    </form>
  );
}

AddReviewDialog.id = 'ADD_REVIEW_DIALOG';
